import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { Provider } from 'react-redux';
import { store } from './store/store';
import LoginPage from './pages/LoginPage';
import DashboardLayout from './components/Layout/DashboardLayout';
import DashboardPage from './pages/DashboardPage';
import ConversationsPage from './pages/ConversationsPage';
import ContactsPage from './pages/ContactsPage';
import CampaignsPage from './pages/CampaignsPage';
import PreChatFormsPage from './pages/PreChatFormsPage';
import AgentCapacityPage from './pages/AgentCapacityPage';
import AgentsPage from './pages/AgentsPage';
import LabelsPage from './pages/LabelsPage';
import TeamsPage from './pages/TeamsPage';
import SlackIntegrationPage from './pages/SlackIntegrationPage';
import InboxesPage from './pages/InboxesPage';
import IntegrationsPage from './pages/IntegrationsPage';
import BusinessHoursPage from './pages/BusinessHoursPage';
import CannedResponsesPage from './pages/CannedResponsesPage';
import SettingsPage from './pages/SettingsPage';
import DocumentationPage from './pages/DocumentationPage';
import ReportsPage from './pages/ReportsPage';
import AutomationRulesPage from './pages/Automation/AutomationRulesPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import RoleManagementPage from './pages/RoleManagementPage';  // Added: 2024-11-12 - User Access Management Module Phase 4.5
import CSATAnalyticsPage from './pages/CSATAnalyticsPage';  // Added: 2025-11-18 - Website Widget Module Phase 4 - Feature 5
import HelpPage from './pages/HelpPage';  // Added: 2025-11-22 - Help & Documentation
import APIDocumentationPage from './pages/APIDocumentationPage';  // Added: 2025-11-22 - API Documentation
import ChangelogPage from './pages/ChangelogPage';  // Added: 2025-11-22 - Changelog
import ForgotPasswordPage from './pages/ForgotPasswordPage';  // Added: 2025-11-22 - Password Reset
import ResetPasswordPage from './pages/ResetPasswordPage';  // Added: 2025-11-22 - Password Reset
import MyProfilePage from './pages/MyProfilePage';  // Added: 2025-11-22 - Enhanced Profile
import WhatsAppTemplatesPage from './pages/WhatsAppTemplatesPage';  // Added: 2026-01-19 - WhatsApp Templates
import { useAppSelector, useAppDispatch } from './store/hooks';
import { KeyboardShortcutsProvider } from './contexts/KeyboardShortcutsContext';
import { fetchUserPermissions } from './store/slices/authSlice'; // 14/11/2025 - User Access Management Module Phase 5.2.1
import EmailTemplatesPage from './pages/EmailTemplatesPage'; // 09/02/2026 - Added for email template management
import TravelerScreeningPage from './pages/TravelerScreeningPage';
import TravelerScreeningPassPage from './pages/TravelerScreeningPassPage';
import PublicPassPage from './pages/PublicPassPage';
import PublicTermsPage from './pages/PublicTermsPage';
import PublicPrivacyPage from './pages/PublicPrivacyPage';
import PublicScreeningPage from './pages/PublicScreeningPage';
import ReportSenderPage from './pages/ReportSenderPage';
import { ColorModeProvider } from './theme/ColorModeContext';

function AppRoutes() {
  const dispatch = useAppDispatch();
  const { user, token, permissionsLoaded } = useAppSelector(state => state.auth);
  const isAuthenticated = !!token && !!user;
  const location = useLocation();

  // 14/11/2025 - User Access Management Module Phase 5.2.1
  // Fetch permissions when user is authenticated but permissions not loaded
  useEffect(() => {
    if (isAuthenticated && !permissionsLoaded) {
      console.log('🔐 Fetching user permissions...');
      dispatch(fetchUserPermissions());
    }
  }, [isAuthenticated, permissionsLoaded, dispatch]);

  return (
    <Routes>
      <Route
        path="/login"
        element={!isAuthenticated ? <LoginPage /> : <Navigate to="/dashboard" />}
      />
      <Route
        path="/forgot-password"
        element={!isAuthenticated ? <ForgotPasswordPage /> : <Navigate to="/dashboard" />}
      />
      <Route
        path="/reset-password"
        element={!isAuthenticated ? <ResetPasswordPage /> : <Navigate to="/dashboard" />}
      />
      <Route
        path="/dashboard/*" 
        element={
          isAuthenticated ? (
            <DashboardLayout>
              <Routes>
                <Route path="" element={<DashboardPage />} />
                <Route path="conversations" element={<ConversationsPage />} />
                <Route path="conversations/:id" element={<ConversationsPage />} />
                <Route path="contacts" element={<ContactsPage />} />
                <Route path="inboxes" element={<InboxesPage />} />
                <Route path="integrations" element={<IntegrationsPage />} />
                <Route path="reports" element={<ReportsPage />} />
                <Route path="automation" element={<AutomationRulesPage />} />
                <Route path="labels" element={<LabelsPage />} />
                <Route path="teams" element={<TeamsPage />} />
                <Route path="agents" element={<AgentsPage />} />
                <Route path="roles" element={<RoleManagementPage />} />  {/* Added: 2024-11-12 - User Access Management Module Phase 4.5 */}
                <Route path="canned-responses" element={<CannedResponsesPage />} />
                <Route path="campaigns" element={<CampaignsPage />} />
                <Route path="pre-chat-forms" element={<PreChatFormsPage />} />
                <Route path="agent-capacity" element={<AgentCapacityPage />} />
                <Route path="slack-integration" element={<SlackIntegrationPage />} />
                <Route path="business-hours" element={<BusinessHoursPage />} />
                <Route path="knowledge-base" element={<KnowledgeBasePage />} />
                <Route path="csat-analytics" element={<CSATAnalyticsPage />} />  {/* Added: 2025-11-18 - Widget CSAT Analytics */}
                <Route path="settings" element={<SettingsPage />} />
                <Route path="documentation" element={<DocumentationPage />} />
                <Route path="help" element={<HelpPage />} />  {/* Added: 2025-11-22 - Help & Documentation */}
                <Route path="api-docs" element={<APIDocumentationPage />} />  {/* Added: 2025-11-22 - API Documentation */}
                <Route path="changelog" element={<ChangelogPage />} />  {/* Added: 2025-11-22 - Changelog */}
                <Route path="my-profile" element={<MyProfilePage />} />  {/* Added: 2025-11-22 - Enhanced Profile */}
                <Route path="whatsapp-templates" element={<WhatsAppTemplatesPage />} />  {/* Added: 2026-01-19 - WhatsApp Templates */}
                <Route path="email-templates" element={<EmailTemplatesPage />} />  {/* Added: 09/02/2026 - Email Templates */}
                <Route path="traveler-screening" element={<TravelerScreeningPage />} />
                <Route path="traveler-screening/:id" element={<TravelerScreeningPassPage />} />
              </Routes>
            </DashboardLayout>
          ) : (
            <Navigate to="/login" state={{ from: location.pathname + location.search }} replace />
          )
        } 
      />
      <Route path="/pass/terms"      element={<PublicTermsPage />} />
      <Route path="/pass/privacy"    element={<PublicPrivacyPage />} />
      <Route path="/pass/:token"    element={<PublicPassPage />} />
      <Route path="/screen"         element={<PublicScreeningPage />} />

      <Route path="/" element={<Navigate to="/dashboard" />} />
      <Route path="/internal" element={<Navigate to="/internal/report-sender" replace />} />
      <Route path="/internal/report-sender" element={<ReportSenderPage />} />
    </Routes>
  );
}

function App() {
  return (
    <Provider store={store}>
      <ColorModeProvider>
        <Router>
          <KeyboardShortcutsProvider>
            <AppRoutes />
          </KeyboardShortcutsProvider>
        </Router>
      </ColorModeProvider>
    </Provider>
  );
}

export default App;